//dependencies
import { useQueryClient } from "@tanstack/react-query";
import React, { useRef, useState } from "react";
import { LoaderFunctionArgs, useParams } from "react-router-dom";

import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import { faAngleDown, faEnvelope } from "@fortawesome/pro-solid-svg-icons";

//components
import { Avatar } from "@performyard/py-components";
import { isSyntheticEmail } from "@performyard/utils-isomorphic";

import ExpandableAvatarGroup from "../../../components/AvatarGroup/ExpandableAvatarGroup";
import Button from "../../../components/Button";
import RotatingIconButton from "../../../components/Button/templates/RotatingIconButton";
import Card from "../../../components/Card";
import Container from "../../../components/Container";
import EmployeeCard from "../../../components/EmployeeCard";
import Icon from "../../../components/Icon/Icon";
import Link from "../../../components/Link";
import Spacer from "../../../components/Spacer";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import Typography from "../../../components/Text";
import AvatarModal from "./components/AvatarModal";
import DeactivateUserModal from "./components/DeactivateUserModal";
import EditEmployeeCard from "./components/EditEmployeeCard";
import ReactivateUserModal from "./components/ReactivateUserModal";
import ReassignManagerModal from "./components/ReassignManagerModal";
import ViewEmployeeCard from "./components/ViewEmployeeCard";

//hooks
import {
	avatarBlobUrlQueryKey,
	useCurrentUser,
	useEmployeeCustomFields,
	useInternalApiUser,
	useRbacEmployeePermissions,
	useSendInvite,
} from "../../../api/hooks";
import useSearchQueryParams from "../../../hooks/useSearchQueryParams";

//pre loader
import {
	currentUserQuery,
	emailTokensQuery,
	employeeCustomFieldsQuery,
	getInternalApiUserQuery,
	rbacEmployeePermissionsQuery,
} from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";

//utils
import { getValueForInputType } from "../utils";

//page tmeplate
import FormPageTemplate from "../../../pageTemplates/FormPageTemplate";
//config
import Config from "../Config";
//styles
import css from "./EmployeeDetails.module.scss";

export const viewEmployeeDetailsLoader = async ({ params }: LoaderFunctionArgs) => {
	const queries: Array<() => SimplifiedQuery> = [];

	if (params?.userID) {
		queries.push(() => rbacEmployeePermissionsQuery(params?.userID || "", "employee_details"));
		queries.push(() => employeeCustomFieldsQuery(params?.userID || ""));
		queries.push(() => getInternalApiUserQuery(params?.userID || ""));
		queries.push(() => emailTokensQuery(params?.userID || ""));
	}

	return await dataLoader([[currentUserQuery], queries]);
};

const EmployeeDetails = () => {
	const { userID = "" } = useParams<{ userID: string }>();
	const { queryParams, handleQueryChange } = useSearchQueryParams({ schema: Config.viewPageSchema });

	const directReportsRef = useRef<HTMLButtonElement>(null);
	const contributesTosRef = useRef<HTMLButtonElement>(null);
	const queryClient = useQueryClient();

	const { data: currentUser, isLoading: isCurrentUserLoading, refetch: refetchCurrentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload?.user_id || "";
	const userId = userID || currentUserId || "";
	const avatarBlobUrl = queryClient.getQueryData<string>(avatarBlobUrlQueryKey(userId));

	const {
		data: userInfo,
		isLoading: isUserLoading,
		refetch: refetchInternalUser,
	} = useInternalApiUser(userId, {
		enabled: !!userId,
	});

	const {
		data = { customFields: [] },
		isLoading,
		refetch: refetchCustomFields,
	} = useEmployeeCustomFields(userId, {
		enabled: !!userId,
	});

	const { data: rbac, isLoading: isRbacLoading } = useRbacEmployeePermissions(userId, "employee_details", !userId);
	const { mutate: invite, isPending: isInviting } = useSendInvite({
		onSuccess: () => {
			refetchInternalUser();
			refetchCustomFields();
			refetchCurrentUser();
		},
	});

	const [directReportsExpanded, setDirectReportsExpanded] = useState<boolean>(false);
	const [contributorsExpanded, setContributorsExpanded] = useState<boolean>(false);

	const canDeactivateEmployee = rbac?.includes("employee_details:deactivateEmployee") && userId !== currentUserId;
	const canEmployeeChangeAvatar = rbac?.includes("employee_details:changeAvatar");
	const isUserSyntheticEmail = isSyntheticEmail(userInfo?.email);

	const customFields = data.customFields;

	const chosenMainCard = queryParams.editing ? (
		<EditEmployeeCard employeeId={userId} />
	) : (
		<ViewEmployeeCard employeeId={userId} />
	);

	const mainCard = isLoading || isCurrentUserLoading || isRbacLoading ? <ComponentLoader /> : chosenMainCard;

	const jobTitle = customFields.find((field) => field.name === "Job Title")?.value;
	const managerField = customFields.find((field) => field.name === "Manager");

	const secondaryColumn = [
		<EmployeeCard
			isLoading={isUserLoading}
			employeeData={{
				id: userInfo?._id.toString() || "",
				name: userInfo?.name,
				isActive: !userInfo?.deactivated,
				jobTitle: String(jobTitle),
				email: userInfo?.email || "",
			}}
			className={css.employeeCard}
			key="employeeCard"
			avatarButtonLabel={
				canEmployeeChangeAvatar && (
					<Container gap="4px">
						<Typography size="paragraph-3" weight="regular" color="neutral-0">
							Edit
						</Typography>
						<Icon size="small" icon={faPencil} color="neutral-0" />
					</Container>
				)
			}
			onAvatarButtonClick={canEmployeeChangeAvatar ? () => handleQueryChange({ modal: "avatar" }) : undefined}
			avatarUrl={avatarBlobUrl}
		/>,
	];

	if (canDeactivateEmployee && !userInfo?.deactivated && queryParams.editing) {
		secondaryColumn.push(
			<Button
				color="red"
				onClick={() => handleQueryChange({ modal: "deactivate" })}
				size="medium"
				key="deactivate-button"
				style={{ width: "100%" }}
			>
				Deactivate Employee
			</Button>
		);
	}

	if (canDeactivateEmployee && userInfo?.deactivated) {
		secondaryColumn.push(
			<Button
				color="red"
				onClick={() => handleQueryChange({ modal: "reactivate" })}
				size="medium"
				key="reactivate-button"
				style={{ width: "100%" }}
			>
				Reactivate Employee
			</Button>
		);
	}

	if (userInfo?.invited && !userInfo?.lastSeen && rbac?.includes("employee_details:sendInvite")) {
		secondaryColumn.push(
			<Button
				style={{ width: "100%" }}
				color="blue"
				onClick={() => {
					invite(userId);
				}}
				size="medium"
				iconPosition="right"
				icon={faEnvelope}
				isRequesting={isInviting}
				disabled={isUserSyntheticEmail}
				key="resendInvite"
			>
				<Typography size="paragraph-3" weight="semi-bold">
					Resend Invite
				</Typography>
			</Button>
		);
	}

	if (!userInfo?.invited && !userInfo?.lastSeen && rbac?.includes("employee_details:sendInvite")) {
		secondaryColumn.push(
			<Button
				style={{ width: "100%" }}
				color="blue"
				onClick={() => {
					invite(userId);
				}}
				size="medium"
				iconPosition="right"
				icon={faEnvelope}
				isRequesting={isInviting}
				disabled={isUserSyntheticEmail}
				key="sendInvite"
			>
				<Typography size="paragraph-3" weight="semi-bold">
					Send Invite
				</Typography>
			</Button>
		);
	}

	const isGroupVisible = customFields.some((field) => field.type === "department" && field.name === "Group");

	if (userInfo?.department && isGroupVisible) {
		secondaryColumn.push(
			<Card padding="8px 16px" key="department">
				<Typography size="paragraph-3" weight="semi-bold">
					Group
				</Typography>
				<Spacer height={1} />
				<Link href={`/group/employees/${userInfo.department._id}/`}>
					<Typography size="paragraph-3" weight="semi-bold">
						{userInfo.department.name}
					</Typography>
				</Link>
			</Card>
		);
	}

	if (userInfo?.departmentAdmin && userInfo.departmentAdmin.length > 0 && isGroupVisible) {
		secondaryColumn.push(
			<Card padding="8px 16px" key="admin-department">
				<Typography size="paragraph-3" weight="semi-bold">
					Groups Managed
				</Typography>
				<Spacer height={1} />
				{userInfo.departmentAdmin.map((group) => (
					<Link href={`/group/employees/${group._id.toString()}/`} key={group._id.toString()}>
						<Typography size="paragraph-3" weight="semi-bold">
							{group.name}
						</Typography>
					</Link>
				))}
			</Card>
		);
	}

	if (managerField && managerField.value) {
		const manager = getValueForInputType(managerField.type, managerField.value);
		secondaryColumn.push(
			<Card padding="8px 16px" key="manager">
				<Typography size="paragraph-3" weight="semi-bold">
					Manager
				</Typography>
				<Spacer height={1} />
				<Link href={`/user/${manager?.value}/`}>
					<Container gap="8px" verticalAlignment="center">
						<Avatar size={24} id={manager?.value} name={manager?.label || "User"} />
						<Typography size="paragraph-3" weight="semi-bold">
							{manager?.label}
						</Typography>
					</Container>
				</Link>
			</Card>
		);
	}

	if (userInfo?.directReports && userInfo.directReports.length > 0) {
		const employees = userInfo.directReports.map((employee) => ({
			name: employee.name,
			_id: employee._id.toString(),
		}));
		secondaryColumn.push(
			<Card padding="8px 16px" key="directReports" onClick={() => directReportsRef?.current?.click()}>
				<Container gap="8px" verticalAlignment="center" horizontalAlignment="space-between">
					<Typography size="paragraph-3" weight="semi-bold">
						Direct Reports
					</Typography>
					{userInfo.directReports.length > 5 && (
						<RotatingIconButton
							icon={faAngleDown}
							onClick={() => setDirectReportsExpanded(!directReportsExpanded)}
							ref={directReportsRef}
							iconOnly
							isHollow={false}
							aria-label="Toggle direct reports"
						/>
					)}
				</Container>
				<Spacer height={1} />
				<ExpandableAvatarGroup
					maxLength={5}
					hasLinks
					employees={employees}
					size={24}
					expanded={directReportsExpanded || userInfo.directReports.length <= 5}
				/>
			</Card>
		);
	}

	if (userInfo?.contributesTo && userInfo.contributesTo.length > 0) {
		const employees = userInfo.contributesTo.map((employee) => ({
			name: employee.name,
			_id: employee._id.toString(),
		}));
		secondaryColumn.push(
			<Card padding="8px 16px" key="contributesTo" onClick={() => contributesTosRef?.current?.click()}>
				<Container gap="8px" verticalAlignment="center" horizontalAlignment="space-between">
					<Typography size="paragraph-3" weight="semi-bold">
						Contributes To
					</Typography>
					{userInfo.contributesTo.length > 5 && (
						<RotatingIconButton
							icon={faAngleDown}
							onClick={() => setContributorsExpanded(!contributorsExpanded)}
							style={{ border: "none", background: "none" }}
							ref={contributesTosRef}
							iconOnly
							isHollow={false}
							aria-label="Toggle contributes to"
						/>
					)}
				</Container>
				<Spacer height={1} />
				<ExpandableAvatarGroup
					maxLength={5}
					hasLinks
					employees={employees}
					size={24}
					expanded={contributorsExpanded || userInfo.contributesTo.length <= 5}
				/>
			</Card>
		);
	}

	return (
		<div>
			<DeactivateUserModal
				isOpen={queryParams.modal === "deactivate"}
				handleClose={() => handleQueryChange({ modal: undefined })}
				userID={userId}
			/>
			<ReactivateUserModal
				isOpen={queryParams.modal === "reactivate"}
				handleClose={() => handleQueryChange({ modal: undefined })}
				userID={userId}
			/>
			<AvatarModal
				isOpen={queryParams.modal === "avatar"}
				handleClose={() => handleQueryChange({ modal: undefined })}
				userID={userId}
			/>
			<ReassignManagerModal
				isOpen={queryParams.modal === "reassign"}
				handleClose={() => handleQueryChange({ modal: undefined, reassignIds: undefined })}
				userID={userId}
			/>
			<FormPageTemplate
				title={`Details - ${userInfo?.name}`}
				mainColumn={[mainCard]}
				secondaryColumn={isLoading || isCurrentUserLoading || isRbacLoading ? [] : secondaryColumn}
				breadcrumbs={[
					{
						pageID: "dashboard",
						label: "Dashboard",
						url: currentUserId === userID ? "/" : `/user/${userId}`,
					},
					{
						pageID: "employee-details",
						label: "Employee Details",
						url: `/details/${userId}`,
					},
				]}
			/>
		</div>
	);
};

export default EmployeeDetails;
